import { useMemo } from "react";
import {
    collectCascadeDescendants,
    collectDescendantValues,
} from "./PickerTree";
import type {
    PickerItemData,
    PickerItemValue,
    PickerParentSelection,
} from "./Picker.types";

export interface UsePickerParentSelectionOptions<T extends PickerItemValue> {
    isMultiple: boolean;
    parentSelection: PickerParentSelection;
    /** The consumer's `selectableParents`; unset falls back to `isMultiple` under `self`. */
    selectableParents: boolean | undefined;
    /** The root's `indicateSelectedDescendants`. */
    indicateSelectedDescendants: boolean;
    /** Flyout levels: the top root's map, built from the unpruned tree. */
    inheritedIndicatedDescendants?: ReadonlyMap<T, readonly T[]>;
    items: ReadonlyArray<PickerItemData<T>> | undefined;
}

/**
 * Resolves how parent rows behave: whether they hold a value of their own
 * and, under `cascade`, which enabled leaves each parent's checkbox toggles.
 * Under cascade a parent is never a value, whatever `selectableParents` says.
 * Under `self` with `indicateSelectedDescendants`, maps each parent to every
 * value below it so an unselected parent with a selected descendant reads
 * indeterminate.
 */
export const usePickerParentSelection = <T extends PickerItemValue>({
    isMultiple,
    parentSelection,
    selectableParents: selectableParentsProp,
    indicateSelectedDescendants,
    inheritedIndicatedDescendants,
    items,
}: UsePickerParentSelectionOptions<T>) => {
    const isCascade = isMultiple && parentSelection === "cascade";
    const selectableParents = isCascade
        ? false
        : (selectableParentsProp ?? isMultiple);
    const cascadeDescendants = useMemo(
        () =>
            isCascade && items !== undefined
                ? collectCascadeDescendants(items)
                : undefined,
        [isCascade, items],
    );
    const isIndicating =
        isMultiple &&
        !isCascade &&
        selectableParents &&
        indicateSelectedDescendants;
    const ownIndicatedDescendants = useMemo(
        () =>
            isIndicating &&
            inheritedIndicatedDescendants === undefined &&
            items !== undefined
                ? collectDescendantValues(items)
                : undefined,
        [isIndicating, inheritedIndicatedDescendants, items],
    );
    return {
        selectableParents,
        cascadeDescendants,
        indicatedDescendants:
            inheritedIndicatedDescendants ?? ownIndicatedDescendants,
    };
};
